/**
 * @fileoverview Seção (por atividade) do drawer de entrega do aluno. Carrega a
 * própria entrega via `useStudentDeliverable` e renderiza: barra do projeto/
 * lição/professor (só na primeira seção), título, due date, instruções, arquivos
 * do professor, upload (`FileUpload` + `GDriveWrapper` legados) e links (AR-678:
 * botão "Add link" ao lado do upload abre o `AddLinkModal`; os links enviados
 * aparecem em "Files and links:" com Submitted at, badge Late e remoção).
 * Sem botão "Submit": upload OU primeiro link auto-submetem (mesmo fluxo:
 * PUT com default_description) e o drawer fecha sozinho após 5s (timer
 * defensivo, cancelado no unmount/close).
 * Entrega já feita (AR-741): a listagem do próprio `FileUpload` (na MESMA entrega,
 * com remoção por arquivo) é a única UI de arquivos; add/remove não re-submete
 * nem fecha o drawer — só toast "Files updated" (links: só o toast "Link added").
 * Defensivo: loading/erro/sem task, guarda `projectId`/`deliveryId`.
 */

import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TypographyStylesProvider,
} from '@mantine/core'
import {
  IconCheck,
  IconFile,
  IconLink,
  IconMusic,
  IconPhoto,
  IconTrash,
  IconVideo,
  IconX,
} from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FileUpload from '@legacy/common/_components/FileUpload'
import { notifications } from 'context'
import { AddLinkModal } from './AddLinkModal'
import { formatLinkSubmittedAt, isLinkLate, type AddLinkOutcome } from './linkUtils'
import {
  useStudentDeliverable,
  type StudentDeliverableFile,
  type StudentDeliverableLink,
} from './useStudentDeliverable'

interface StudentDeliverableSectionProps {
  activityId: string
  opened: boolean
  showHeader: boolean
  withDivider: boolean
  onSubmitted: () => void
}

const AUTO_CLOSE_DELAY_MS = 5000

function FileIcon({ fileType }: { fileType: string }) {
  if (fileType === 'image') return <IconPhoto size={14} />
  if (fileType === 'video') return <IconVideo size={14} />
  if (fileType === 'audio') return <IconMusic size={14} />
  return <IconFile size={14} />
}

function FileLink({ file }: { file: StudentDeliverableFile }) {
  return (
    <Anchor href={file.downloadUrl} target="_blank" rel="noopener noreferrer" size="sm">
      <Group spacing={6} noWrap>
        <FileIcon fileType={file.fileType} />
        <Text size="sm" lineClamp={1}>{file.name}</Text>
      </Group>
    </Anchor>
  )
}

const LabeledValue = ({ label, value }: { label: string; value: string }) => (
  <Text size="sm">
    <Text span color="dimmed">{label}: </Text>
    <Text span weight={500}>{value}</Text>
  </Text>
)

interface SubmittedLinkItemProps {
  link: StudentDeliverableLink
  late: boolean
  submittedAtLabel: string
  lateLabel: string
  formattedSubmittedAt: string
  removing: boolean
  onRemove: (linkId: string) => void
}

// Linha de link enviado: URL completa clicável (quebra em qualquer ponto para
// não estourar o drawer), "Submitted at" + badge Late e remoção.
function SubmittedLinkItem({
  link,
  late,
  submittedAtLabel,
  lateLabel,
  formattedSubmittedAt,
  removing,
  onRemove,
}: SubmittedLinkItemProps) {
  return (
    <Paper withBorder radius="md" p="xs">
      <Stack spacing={4}>
        <Group spacing="xs" position="apart" noWrap>
          <Anchor
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}
          >
            <Group spacing={6} noWrap align="flex-start">
              <Box sx={{ flexShrink: 0 }}><IconLink size={16} /></Box>
              <Text size="sm" sx={{ wordBreak: 'break-all' }}>{link.url}</Text>
            </Group>
          </Anchor>
          <ActionIcon
            variant="subtle"
            color="red"
            loading={removing}
            onClick={() => onRemove(link.id)}
            aria-label={`remove-link-${link.id}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
        {formattedSubmittedAt && (
          <Group spacing={6} align="center">
            <Text size="xs" color="dimmed">{`${submittedAtLabel}: ${formattedSubmittedAt}`}</Text>
            {late && <Badge color="red" size="sm">{lateLabel}</Badge>}
          </Group>
        )}
      </Stack>
    </Paper>
  )
}

export function StudentDeliverableSection({
  activityId,
  opened,
  showHeader,
  withDivider,
  onSubmitted,
}: StudentDeliverableSectionProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'pt-BR'
  const { loading, errored, submitted, data, links, submit, addLink, removeLink } =
    useStudentDeliverable(activityId, opened)
  const tr = (key: string) => t(`task_tracker.deliverable_drawer.${key}`)

  const [linkModalOpened, setLinkModalOpened] = useState(false)
  // Concurrent DELETEs: one spinner per link being removed.
  const [removingLinkIds, setRemovingLinkIds] = useState<ReadonlySet<string>>(new Set())

  // Auto-close timer: keep the id in a ref so we can clear it on unmount/close and
  // never fire `onSubmitted()` (which calls setState upstream) after the drawer is gone.
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current)
      autoCloseTimer.current = null
    }
  }, [])

  // Cancel any pending auto-close when the section unmounts or the drawer closes.
  useEffect(() => clearAutoCloseTimer, [clearAutoCloseTimer])
  useEffect(() => {
    if (!opened) clearAutoCloseTimer()
  }, [opened, clearAutoCloseTimer])

  // First-submission flow, shared by files and links: auto-submit with the
  // default description (no text field in the design), warn the user and
  // schedule the auto-close after 5s. Single timer owner — never duplicated.
  const submitAndScheduleClose = useCallback(async () => {
    const ok = await submit(tr('default_description'))
    notifications.show({
      message: ok ? tr('auto_submit_success') : tr('submit_error'),
      color: ok ? 'green' : 'red',
      icon: ok ? <IconCheck size={20} /> : <IconX size={20} />,
    })
    if (!ok) return

    clearAutoCloseTimer()
    autoCloseTimer.current = setTimeout(() => {
      autoCloseTimer.current = null
      onSubmitted()
    }, AUTO_CLOSE_DELAY_MS)
  }, [submit, onSubmitted, clearAutoCloseTimer, tr])

  // Fired by the legacy `FileUpload` via `successUploadCallback` after an upload
  // OR a delete finishes. Already submitted (AR-741): the files changed on the
  // SAME done delivery, so skip the re-PUT and the auto-close — just notify.
  const handleAutoSubmit = useCallback(async () => {
    if (submitted) {
      clearAutoCloseTimer()
      notifications.show({
        message: tr('files_updated'),
        color: 'green',
        icon: <IconCheck size={20} />,
      })
      return
    }

    await submitAndScheduleClose()
  }, [submitted, submitAndScheduleClose, clearAutoCloseTimer, tr])

  // Add link (AR-678): POST via hook; sucesso → toast verde e, se a entrega
  // ainda não foi submetida, dispara o MESMO fluxo de auto-submit dos arquivos.
  // 422 → o modal mostra o erro inline; outros erros → toast vermelho.
  const handleAddLink = useCallback(
    async (url: string): Promise<AddLinkOutcome> => {
      const outcome = await addLink(url)
      if (outcome === 'success') {
        notifications.show({
          message: tr('link_added'),
          color: 'green',
          icon: <IconCheck size={20} />,
        })
        if (!submitted) await submitAndScheduleClose()
      } else if (outcome === 'error') {
        notifications.show({
          message: tr('link_add_error'),
          color: 'red',
          icon: <IconX size={20} />,
        })
      }
      return outcome
    },
    [addLink, submitted, submitAndScheduleClose, tr],
  )

  const handleRemoveLink = useCallback(
    async (linkId: string) => {
      setRemovingLinkIds(current => new Set(current).add(linkId))
      const ok = await removeLink(linkId)
      setRemovingLinkIds(current => {
        const next = new Set(current)
        next.delete(linkId)
        return next
      })
      if (!ok) {
        notifications.show({
          message: tr('link_remove_error'),
          color: 'red',
          icon: <IconX size={20} />,
        })
      }
    },
    [removeLink, tr],
  )

  if (loading) return <Center style={{ height: 120 }}><Loader size="sm" /></Center>
  if (errored || !data) {
    return (
      <Center style={{ height: 120 }}>
        <Text color="dimmed" size="sm">{tr('error_loading')}</Text>
      </Center>
    )
  }

  const taskFiles = data.planningFiles ?? []
  const canUpload = Boolean(data.projectId && data.studentTaskDeliveryId)
  const filesPath = `/projects/${data.projectId}/student_task_deliveries/${data.studentTaskDeliveryId}`
  const activity = data.sessionMeetingActivity

  return (
    <>
      {withDivider && <Divider />}
      {showHeader && (
        <Paper withBorder radius="md" p="md">
          <Group position="apart" align="flex-start" noWrap>
            <Stack spacing={2}>
              <LabeledValue label={tr('project_label')} value={activity.projectName} />
              <LabeledValue label={tr('lesson_label')} value={activity.sessionMeetingTitle} />
            </Stack>
            {activity.tutor && (
              <Box sx={{ flexShrink: 0 }}>
                <LabeledValue label={tr('created_by')} value={activity.tutor} />
              </Box>
            )}
          </Group>
        </Paper>
      )}

      <Stack spacing="xs">
        <Group spacing="xs" align="center">
          <Text size="md" weight={700}>{`${activity.title} - ${data.title}`}</Text>
          {submitted && <Badge color="green" size="sm">{tr('submitted_badge')}</Badge>}
          {data.overdue && <Badge color="red" size="sm">{tr('late_badge')}</Badge>}
        </Group>
        {data.deadline && <LabeledValue label={tr('due_date')} value={data.deadline} />}
        {data.description && (
          <div>
            <Text size="sm" color="dimmed">{tr('instructions')}:</Text>
            <TypographyStylesProvider
              sx={{
                fontSize: '0.875rem',
                '& > :first-of-type': { marginTop: 0 },
                '& > :last-child': { marginBottom: 0 },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: data.description }} />
            </TypographyStylesProvider>
          </div>
        )}
      </Stack>

      {taskFiles.length > 0 && (
        <Stack spacing="xs">
          <Text size="sm" color="dimmed">{tr('task_files')}:</Text>
          {taskFiles.map(file => <FileLink key={file.id} file={file} />)}
        </Stack>
      )}

      {canUpload && (
        <Stack spacing="sm">
          <Text size="sm" weight={600}>{tr('your_submission')}</Text>
          <Group spacing="sm" align="flex-start" noWrap>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <div className="fileContainer" key={data.studentTaskDeliveryId}>
                {/* The lesson drawer mounts one section per activity and FileUpload builds the
                    hidden input id from fileUploadId, so it must be unique per section or every
                    "Upload file" label activates the first input in the document. */}
                <FileUpload
                  uploadUrl={`${filesPath}/planning_files`}
                  fetchFilesUrl={`${filesPath}/planning_files.json`}
                  deleteFilesUrl={`${filesPath}/planning_files`}
                  typeFile="planning_files"
                  fileUploadId={`student_task_deliveries-${activityId}`}
                  appliedFile={false}
                  successUploadCallback={handleAutoSubmit}
                />
              </div>
            </Box>
            <Button
              variant="outline"
              leftIcon={<IconLink size={14} />}
              onClick={() => setLinkModalOpened(true)}
              disabled={!data.studentTaskDeliveryId}
            >
              {tr('add_link')}
            </Button>
          </Group>

          {/* AR-805: no Classroom block here — this is the teacher's mirror of
              the delivery, and GET student_tasks/:id/google_classroom_files
              answers 401 for anyone but the student. Imported submissions show
              up as ordinary files/links (source: "google_classroom"). */}

          {links.length > 0 && (
            <Stack spacing="xs">
              <Text size="sm" weight={600}>{tr('files_and_links')}</Text>
              {links.map(link => (
                <SubmittedLinkItem
                  key={link.id}
                  link={link}
                  late={isLinkLate(link.createdAt, data.deadline)}
                  submittedAtLabel={tr('submitted_at')}
                  lateLabel={tr('late_badge')}
                  formattedSubmittedAt={formatLinkSubmittedAt(link.createdAt, locale)}
                  removing={removingLinkIds.has(link.id)}
                  onRemove={handleRemoveLink}
                />
              ))}
            </Stack>
          )}

          <AddLinkModal
            opened={linkModalOpened}
            onClose={() => setLinkModalOpened(false)}
            onAdd={handleAddLink}
          />
        </Stack>
      )}
    </>
  )
}
